import { api } from '@/features/auth';

import {
  CreateOfferRequest,
  GetOfferRequest,
  GetOfferResponse,
  GetOffersResponse,
  UpdateOfferAvailabilityRequest,
  UpdateOfferFeedbackRequest,
  UpdateOfferGeneratedPdfRequest,
  UpdateOfferMeetingTimeRequest,
  UpdateOfferSlotManualRequest,
  UpdateOfferStatusRequest,
} from './types';

export const offersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMyOffers: builder.query<GetOffersResponse['data'], void>({
      query: () => ({
        url: `items/offers`,
        method: 'GET',
        params: {
          fields: '*, opportunity.*, opportunity.projects.*.*, opportunity.projects.project.technologies.*.*',
          filter: `{"company": {"users": {"_eq": "$CURRENT_USER"}}, "status": { "_nin": ["archived", "internal_rejected", "published"]}}`,
          sort: '-updated_at',
        },
      }),
      transformResponse: ({ data }: GetOffersResponse) => data,
      providesTags: [{ type: 'Offers', id: 'me' }],
    }),
    getOffer: builder.query<GetOfferResponse['data'], GetOfferRequest>({
      query: ({ id }: GetOfferRequest) => ({
        url: `items/offers/${id}`,
        method: 'GET',
        params: {
          fields: [
            // Specific data from offer:
            'id',
            'created_at',
            'created_by',
            'updated_at',
            'updated_by',
            'status',
            'buyer_type',
            'buyer_comments',
            'buyer_website',
            'buyer_presentation',
            'file',
            'comments',
            'rating_comments',
            'express_interest',
            'eoi_file',
            'availability',
            'meeting_time',
            'transaction_progress',
            'submitted_eoi_file',
            'is_submitted_eoi_file_generated',
            'feedback_reject',
            'internal_notes_reject',

            // Relations with other collections:
            'opportunity.*',
            'opportunity.options.*',
            'company.id',
            'company.name',

            // TODO - get ONLY essential fields:
            'opportunity.projects.*.*',

            // TODO - get ONLY essential fields:
            'opportunity.projects.project.technologies.*.*',
          ],
          filter: '{"status": { "_nin": ["archived", "published"]}}',
        },
      }),
      transformResponse: ({ data }: GetOfferResponse) => data,
      providesTags: (_result, _error, args) => [{ type: 'Offers', id: args.id }],
    }),
    createOffer: builder.mutation<void, CreateOfferRequest>({
      query: (data: CreateOfferRequest) => ({
        url: `items/offers`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Offers', id: 'me' }],
    }),
    updateOfferStatus: builder.mutation<void, UpdateOfferStatusRequest>({
      query: ({ id, data }: UpdateOfferStatusRequest) => ({
        url: `items/offers/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Offers', id: args.id },
        { type: 'Offers', id: args.opportunity },
      ],
    }),
    updateOfferFeedback: builder.mutation<void, UpdateOfferFeedbackRequest>({
      query: ({ id, data }: UpdateOfferFeedbackRequest) => ({
        url: `items/offers/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [{ type: 'Offers', id: args.id }],
    }),
    updateOfferAvailabilityAndStatus: builder.mutation<void, UpdateOfferAvailabilityRequest>({
      query: ({ id, data }: UpdateOfferAvailabilityRequest) => ({
        url: `items/offers/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Offers', id: args.id },
        { type: 'Offers', id: args.opportunity },
      ],
    }),
    updateOfferMeetingTime: builder.mutation<void, UpdateOfferMeetingTimeRequest>({
      query: ({ id, data }: UpdateOfferMeetingTimeRequest) => ({
        url: `items/offers/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [{ type: 'Offers', id: args.id }],
    }),
    updateOfferGeneratedPdf: builder.mutation<void, UpdateOfferGeneratedPdfRequest>({
      query: ({ id, data }: UpdateOfferGeneratedPdfRequest) => ({
        url: `items/offers/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [{ type: 'Offers', id: args.id }],
    }),
    updateOfferSlotManualRequest: builder.mutation<void, UpdateOfferSlotManualRequest>({
      query: ({ id, data }: UpdateOfferSlotManualRequest) => ({
        url: `items/offers/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [{ type: 'Offers', id: args.id }],
    }),
  }),
});

export const {
  useGetOfferQuery,
  useGetMyOffersQuery,
  useCreateOfferMutation,
  useUpdateOfferStatusMutation,
  useUpdateOfferFeedbackMutation,
  useUpdateOfferAvailabilityAndStatusMutation,
  useUpdateOfferMeetingTimeMutation,
  useUpdateOfferGeneratedPdfMutation,
  useUpdateOfferSlotManualRequestMutation,
} = offersApi;

export type { GetOfferRequest, GetOfferResponse, GetOffersResponse } from './types';
